import React from 'react';
import { useMutation } from '@apollo/client';
import { Button, Typography, Loading, Spacer } from '@guildeducationinc/recess';
import { GuildUser } from '@guildeducationinc/guild-auth';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { datadogRum } from '@datadog/browser-rum';
import { UpdateUserResponse, UpdateUserInput } from '@src/utilities/userProfileUtility';
import Divider from '@src/components/Shared/Divider/Divider';
import RouteLeavingGuard from '@src/components/Shared/RouteLeavingGuard/RouteLeavingGuard';
import TextInputField from '@src/components/Shared/FormInputFields/TextInputField/TextInputField';
import { UPDATE_UPS } from '@src/graphql/profile_edit/mutations/updateProfile';
import { Server } from '@src/components/GqlProvider/GqlProvider';
import '../EditProfile.scss';
import { clickable } from '@src/analytics';

interface FormValues {
  firstName: string;
  lastName: string;
}

type Props = {
  formData?: GuildUser;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  setShowError: React.Dispatch<React.SetStateAction<any>>;
  setShowSuccess: React.Dispatch<React.SetStateAction<any>>;
};

const validationSchema = Yup.object({
  firstName: Yup.string().required('Please enter your first name.'),
  lastName: Yup.string().required('Please enter your last name.'),
});

const BasicInformation: React.FC<Props> = ({
  formData,
  setFormData,
  setShowError,
  setShowSuccess,
}) => {
  const [updateUserProfile] = useMutation<UpdateUserResponse, UpdateUserInput>(UPDATE_UPS, {
    context: {
      server: Server.UPSV2,
    },
  });

  const onSubmit = async (values: FormValues) => {
    try {
      const updateUserInput = {
        guildUuid: formData?.id as string,
        firstName: values.firstName,
        lastName: values.lastName,
      };
      await updateUserProfile({
        variables: {
          updateUserInput,
        },
      });
      clickable({
        product: 'account',
        loggedIn: true,
        tag: 'account_profile',
        text: 'save_changes',
        type: 'button',
        location: 'basic_information',
      });
      setFormData({ ...formData, ...values });
      setShowSuccess(true);
      setShowError(false);
    } catch (error) {
      datadogRum.addError(error, {
        message: 'There was an error when saving changes to basic information',
      });
      setShowSuccess(false);
      setShowError(true);
    }
  };

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        firstName: formData?.firstName ?? '',
        lastName: formData?.lastName ?? '',
      }}
      onSubmit={(values) => onSubmit(values)}
      validationSchema={validationSchema}
    >
      {({ dirty, handleBlur, handleChange, handleSubmit, isSubmitting, isValid, values }) => (
        <>
          <RouteLeavingGuard when={dirty} />
          <Loading
            label='edit profile'
            loaded={!isSubmitting}
            showChildrenWhileLoading={true}
            overlay={true}
          >
            <Typography variant='h3'>Basic information</Typography>
            <Typography variant='body1' className='padding--y_8'>
              * Required
            </Typography>
            <Typography variant='body1' className='padding--bottom_32'>
              Make sure the name you enter matches your government ID.
            </Typography>
            <Form>
              <div className='accountFormFields padding--bottom_24'>
                <Spacer pb={24}>
                  <TextInputField
                    label='First name'
                    required={true}
                    name='firstName'
                    placeholder='First name'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.firstName}
                  />
                </Spacer>
                <Spacer pb={24}>
                  <TextInputField
                    label='Last name'
                    required={true}
                    name='lastName'
                    placeholder='Last name'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.lastName}
                  />
                </Spacer>
              </div>
              <Divider color='black-lighten-87' />
              <div className='flex flexJustify--end padding--top_24'>
                <Button
                  type='button'
                  onClick={() => {
                    handleSubmit();
                  }}
                  disabled={!dirty || !isValid || isSubmitting}
                  testid='account-save-button'
                >
                  Save changes
                </Button>
              </div>
            </Form>
          </Loading>
        </>
      )}
    </Formik>
  );
};

export default BasicInformation;
